import styled from '../../../theme/styled-with-theme';
import { pxToRem } from 'src/styles/utils';
import { mediaQueries } from '../../../styles/media-queries';

import { TextInputWidth } from '../shared';

import arrowDown from '@/images/arrow-down.svg';
import whiteCheck from '@/images/multi-check.svg';
import searchIcon from '@/images/search-green-icon.svg';

export const SearchIcon = styled('div')`
  background-image: url(${searchIcon.src});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
`;

export const MultiSelectWrapper = styled('div')<{
  width: TextInputWidth;
  error: boolean;
  variant?: 'default' | 'preferences' | 'stateModal';
}>`
  position: relative;
  width: ${({ width }) => width ?? '100'}%;
  margin-bottom: ${({ error, variant = 'default' }) => {
    if (variant === 'default') {
      return ``;
    }
    return error ? pxToRem(8) : pxToRem(16);
  }};
`;

export const Dropdown = styled('div')<{
  width: TextInputWidth;
  height?: number;
  error: boolean;
  selected: boolean;
  variant?: 'default' | 'preferences' | 'stateModal';
}>`
  width: ${({ width }) => width}%;
  height: ${({ height }) => (height ? pxToRem(height) : pxToRem(32))};
  display: flex;
  justify-content: flex-start;
  align-items: center;
  border: 1px solid
    ${({ theme, error }) =>
      error ? theme.color.textInputInvalid : theme.color.textInputPrimary};
  border-radius: ${({ theme }) => theme.borderRadius.input_desktop};
  transition:
    border 0.3s,
    margin-bottom 0.3s,
    background-color 0.3s;
  color: ${({ theme }) => theme.color.secondaryText};
  line-height: 1.5;
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;

  background-color: ${({ theme }) => theme.color.white};
  background-image: url(${arrowDown.src});
  background-repeat: no-repeat;
  background-position: calc(100% - ${pxToRem(22)}) center;
  background-size: ${pxToRem(12)};

  &:focus {
    outline: none;
  }

  padding: 3px 15px;

  ${({ selected, variant }) => {
    if (selected && variant === 'default') {
      return `
        background-color: #E6F9F5;
        border-color: #60AFB9;
      `;
    }
    if (selected && variant === 'preferences') {
      return `
        font-weight: 400;
        color: #1D252C;
      `;
    }
    if (variant === 'preferences') {
      return `
        font-weight: 500;
        color: #1D252C;
      `;
    }
  }}
`;

export const CheckboxesWrapper = styled('div')<{
  visible: boolean;
  minified: boolean;
  variant?: 'default' | 'preferences' | 'stateModal';
}>`
  padding: 1.65rem;
  position: absolute;
  min-width: 10rem;
  height: 20vh;

  overflow-x: auto;

  background: #ffffff;

  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.16);
  border-radius: 25px;

  position: absolute;
  z-index: 1;
  transition:
    top 0.3s,
    opacity 0.3s,
    visibility 0s;
  top: 0;
  opacity: 0;
  visibility: hidden;

  display: grid;
  grid-gap: 10px;
  grid-column-gap: 3rem;
  grid-template-rows: 'none';
  grid-auto-flow: 'column';

  ${mediaQueries.tablet} {
    min-width: max-content;
    max-width: 23rem;
    height: inherit;

    grid-template-rows: ${({ minified }) =>
      minified ? 'none' : 'repeat(8, 1fr)'};
    grid-auto-flow: ${({ minified }) => (minified ? 'row' : 'column')};

    ${({ variant = 'default' }) => {
      switch (variant) {
        case 'stateModal':
          return `
            min-width: 100px;
            width: 100%;
            overflow-x: auto;
            grid-auto-flow: row;
            height: 25vh;
          `;

        case 'preferences':
        default:
          return ``;
      }
    }}
  }

  ${({ visible }) => {
    if (visible) {
      return `
        opacity: 1;
        top: 50px;
        visibility: visible;
      `;
    }
  }}
`;

export const CheckboxWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing['m']};
  height: ${({ theme }) => theme.lineHeight['smm']};
`;

export const CheckboxInput = styled('input')`
  -webkit-appearance: none;
  appearance: none;
  background-color: #fff;
  margin: 0;
  font: inherit;
  width: ${pxToRem(12)};
  height: ${pxToRem(12)};
  border: 2px solid #c6c8ca;
  border-radius: ${pxToRem(4)};
  transform: translateY(-0.075em);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: ${pxToRem(8)};
  position: relative;
  transition: background-color 0.3s;

  &[disabled] {
    background: rgba(0, 0, 0, 0.05);
  }

  &:checked {
    background-color: #00d9b4;
    border: 2px solid transparent;

    &:after {
      content: url(${whiteCheck.src});
      position: absolute;
      object-fit: cover;
      width: ${pxToRem(12)};
      height: ${pxToRem(12)};
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${({ theme }) => theme.color.white};
      bottom: 3px;
    }
  }
`;

export const CheckboxLabel = styled('label')`
  font-weight: ${({ theme }) => theme.fontWeight['500']};
  color: ${({ theme }) => theme.color.black};
  font-weight: ${({ theme }) => theme.fontSize['sm']};
  line-height: ${({ theme }) => theme.lineHeight['smm']};
`;

export const InputLabel = styled('label')<{
  floatLabel: boolean;
}>`
  position: absolute;
  transition:
    margin-top 0.3s,
    font-size 0.3s,
    margin-left 0.3s,
    padding 0.3s;
  border-radius: ${({ theme }) => theme.borderRadius.input_desktop};
  font-weight: 500;
  font-size: ${({ floatLabel }) => (floatLabel ? '.8em' : '1em')};
  padding: ${({ floatLabel }) =>
    floatLabel ? '0 4px 0 4px' : `${pxToRem(22)} 0 0 ${pxToRem(16)}`};
  ${({ floatLabel }) =>
    floatLabel
      ? `
      margin-top: -8px;
      margin-left: 16px;
      background-color: #fff;
    `
      : ''}
`;
